import { useEffect, useRef } from "react";

import type { ExtendedUIMessage } from "../chat.types";
import type { ChatTranscriptDimensions } from "../providers/chat-metrics-provider";
import { useChatMetricsObserver } from "../providers/chat-metrics-provider";

/**
 * Floor between two measurements of the same session. Measuring means serializing the whole
 * transcript, which on the tool-heavy sessions this exists to characterize is the exact main-thread
 * work we are trying to observe — so it runs on a throttle rather than on every streamed token.
 */
export const TRANSCRIPT_MEASUREMENT_INTERVAL_MS = 5000;

/** Only the shape this measures — nothing here reads a part's contents. */
type MeasurableMessage = Pick<ExtendedUIMessage, "parts">;

/**
 * Serialized size is an estimate by construction: a JSON string's `length` counts UTF-16 code
 * units, not bytes. Left `undefined` when the transcript cannot be serialized at all, so the reader
 * sees "not measured" instead of a zero that reads as an empty transcript.
 */
const estimateSerializedSize = (messages: readonly MeasurableMessage[]): number | undefined => {
  try {
    return JSON.stringify(messages)?.length;
  } catch {
    return undefined;
  }
};

export const measureTranscript = (
  sessionId: string,
  messages: readonly MeasurableMessage[]
): ChatTranscriptDimensions => {
  let partCount = 0;
  for (const message of messages) {
    // `parts` is non-optional on the type but absent on real rows often enough that the render path
    // guards it too (`assistant-progress-state`, `user-chat-session-message`); a throw here would
    // come out of an effect on the path this exists to keep alive.
    partCount += message.parts?.length ?? 0;
  }

  return {
    sessionId,
    entryCount: messages.length,
    partCount,
    estimatedBytes: estimateSerializedSize(messages),
  };
};

/**
 * Report how large the rendered transcript has grown to whatever host adapter is mounted, and tell
 * it when the session stops being rendered so per-session numbers can't outlive their session.
 *
 * No-ops entirely when no `ChatMetricsProvider` is above it.
 */
export const useTranscriptDimensions = (sessionId: string, messages: readonly MeasurableMessage[]): void => {
  const observer = useChatMetricsObserver();
  const lastMeasuredRef = useRef({ sessionId: "", at: 0 });

  useEffect(() => {
    return () => {
      observer?.onSessionEnd(sessionId);
    };
  }, [observer, sessionId]);

  useEffect(() => {
    if (!observer) {
      return;
    }

    const report = () => {
      lastMeasuredRef.current = { sessionId, at: Date.now() };
      observer.onTranscriptDimensions(measureTranscript(sessionId, messages));
    };

    // A session the throttle has never seen reports immediately; only a repeat measurement of the
    // same session waits out the window.
    const isFirstOfSession = lastMeasuredRef.current.sessionId !== sessionId;
    const waitMs = isFirstOfSession ? 0 : lastMeasuredRef.current.at + TRANSCRIPT_MEASUREMENT_INTERVAL_MS - Date.now();
    if (waitMs <= 0) {
      report();
      return;
    }

    // Trailing measurement: a stream that settles inside the throttle window still gets its final
    // size reported, rather than leaving the session recorded at whatever it was mid-stream.
    const timer = setTimeout(report, waitMs);
    return () => clearTimeout(timer);
  }, [messages, observer, sessionId]);
};
